import axios, { AxiosResponse } from 'axios';
import {
  MovementMediaDto,
  MovementResultsOutputDto,
  PlanRevisionDto,
  TreatmentPlanDto,
  TreatmentPlanInputDto,
  TreatmentPlanTemplateDto,
  TreatmentPlanTemplateInputDto,
} from '../types/backendType';
import httpClient from './httpClient';

//GET /treatment-plan/assignment
export async function fetchUserTreatmentPlans({
  userId,
  startDate,
  endDate,
}: {
  userId: string;
  startDate: Date;
  endDate: Date;
}): Promise<TreatmentPlanDto[] | undefined> {
  try {
    const response = await httpClient.get<TreatmentPlanDto[]>('/treatment-plan/assignment', {
      params: {
        executed_plan: true,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      },
      headers: { 'x-user-id': userId }, //TODO: remove this and check if interceptor working
    });
    const treatmentPlanDtoArray = response.data;
    return treatmentPlanDtoArray;
  } catch (e) {
    throw new Error(`Failed to fetch movement treatment plans: ${e}`);
  }
}

// GET /treatment-plan/assignment/{plan_id} — one plan as the server holds it NOW. Used while a
// clinician edits a plan during a live session, to pick up the patient moving an exercise to the
// end (PAC-127). Errors propagate: the caller polls, and a failed poll just waits for the next.
export async function fetchTreatmentPlanById({
  planId,
  patientId,
}: {
  planId: string;
  patientId: string;
}): Promise<TreatmentPlanDto> {
  const response = await httpClient.get<TreatmentPlanDto>(
    `/treatment-plan/assignment/${planId}`,
    {
      headers: { 'x-user-id': patientId },
    },
  );
  return response.data;
}

//GET /movement/results
export async function fetchMovementsResults({
  userId,
  startDate,
  endDate,
}: {
  userId: string;
  startDate: Date;
  endDate?: Date;
}): Promise<MovementResultsOutputDto | undefined> {
  try {
    const response = await httpClient.get<MovementResultsOutputDto>('/movement/results', {
      params: {
        start_date: startDate.toISOString(),
        ...(endDate ? { end_date: endDate.toISOString() } : {}),
        results_data_only: true,
        ignore_rpe_survey: false,
      },
      headers: { 'x-user-id': userId }, //TODO: remove this and check if interceptor working
    });
    const movementResultsOutputDto = response.data;
    return movementResultsOutputDto;
  } catch (e) {
    throw new Error(`Failed to movement results: ${e}`);
  }
}

// GET /streaming/movement/metrics/{session_id}/{sub_session_id}
//
// Returns the signed-URL string when the backend has metrics for this
// sub-session. Returns `undefined` for the EXPECTED 404 case — a
// skipped/non-analyzed movement has no metrics file, and that's a
// normal day-data outcome, not an error. Skipped movements are
// common during a normal session (clinician removes an exercise
// mid-flow), so the qualitative-violations pre-population code calls
// this for every sub-session and silently skips the empty ones.
//
// 404 is the ONLY status we accept as "no data"; anything else (5xx,
// auth) still throws so a real outage surfaces.
export async function fetchMovementExecutionMetricsUrl({
  patientId,
  sessionId,
  subSessionId,
  version,
}: {
  patientId: string;
  sessionId: string;
  subSessionId: string;
  version?: number;
}): Promise<string | undefined> {
  try {
    const response = await httpClient.get<MovementMediaDto>(
      `/streaming/movement/metrics/${sessionId}/${subSessionId}`,
      {
        headers: { 'x-user-id': patientId },
        params: { version },
        // Tell axios 404 is a valid response — the response interceptor
        // and the caller-side catch then don't surface the missing
        // metrics as a console error. Anything else still throws.
        validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
      },
    );

    if (response.status === 404) {
      return undefined;
    }
    const movementMediaDto = response.data;
    if (!movementMediaDto) {
      return undefined;
    }
    return movementMediaDto.mediaLink;
  } catch (e) {
    throw new Error(`Failed to fetch movement metrics : ${e}`);
  }
}

// GET /streaming/movement/media/{session_id}/{sub_session_id}
export async function fetchMovementExecutionVideoUrl({
  patientId,
  sessionId,
  subSessionId,
  preferBlurred = false,
}: {
  patientId: string;
  sessionId: string;
  subSessionId: string;
  // A patient sees the face-blurred copy the offline blur job writes next to the video
  // (`blurred_video_...`). Until every video has one, a missing copy falls back to the original.
  preferBlurred?: boolean;
}): Promise<MovementMediaDto | undefined> {
  if (preferBlurred) {
    try {
      const blurred = await httpClient.get<MovementMediaDto>(
        `/streaming/movement/media/${sessionId}/${subSessionId}`,
        {
          headers: { 'x-user-id': patientId },
          params: { version: 2, variant: 'blurred' },
          telemetryExpectedStatuses: [404],
        },
      );
      if (blurred.data) {
        return blurred.data;
      }
    } catch (e) {
      // No blurred copy yet: fall through to the original.
    }
  }
  try {
    let response: AxiosResponse<MovementMediaDto, any>;
    try {
      // `version: 2` selects the CURRENT S3 prefix (`video_<subSession>`); omitting it selects
      // the legacy one (`<subSession>_raw`). Both 404 through the same MovementMediaNotFound
      // path, so a movement with no video at all necessarily costs BOTH requests — the probe
      // cannot tell "recorded under the old name" from "never recorded" without trying.
      //
      // The v2 404 is therefore an expected step of a two-prefix lookup, not a failure, and
      // reporting it doubled every media-less movement in RUM: 29 of the 115 portal failures
      // on 2026-09-04 were `HTTP_404:LoadMovementMedia`. Only the FALLBACK's failure is real,
      // and it is still recorded — so a genuine missing upload (see the [S3-UPLOAD-LOST] path
      // in core #1512) still surfaces.
      response = await httpClient.get<MovementMediaDto>(
        `/streaming/movement/media/${sessionId}/${subSessionId}`,
        {
          headers: { 'x-user-id': patientId },
          params: { version: 2 },
          telemetryExpectedStatuses: [404],
        },
      );
    } catch (e) {
      response = await httpClient.get<MovementMediaDto>(
        `/streaming/movement/media/${sessionId}/${subSessionId}`,
        {
          headers: { 'x-user-id': patientId },
        },
      );
    }
    const movementMediaDto = response.data;
    if (!movementMediaDto) {
      throw new Error('movementMediaDto is empty');
    }
    return movementMediaDto;
  } catch (e) {
    throw new Error(`Failed to fetch movement media : ${e}`);
  }
}

//POST /treatment-plan/template
export async function createTreatmentPlanTemplate({
  treatmentPlanTemplateInputDto,
}: {
  treatmentPlanTemplateInputDto: TreatmentPlanTemplateInputDto;
}): Promise<TreatmentPlanTemplateDto> {
  try {
    const response = await httpClient.post<TreatmentPlanTemplateDto>(
      '/treatment-plan/template',
      treatmentPlanTemplateInputDto,
    );
    const treatmentPlanTemplateDto = response.data;
    return treatmentPlanTemplateDto;
  } catch (e) {
    throw new Error(`Failed to create treatment plan template: ${e}`);
  }
}

//GET /treatment-plan/template
export async function fetchTreatmentPlanTemplates(): Promise<
  TreatmentPlanTemplateDto[] | undefined
> {
  try {
    const response = await httpClient.get<TreatmentPlanTemplateDto[]>(
      '/treatment-plan/template',
    );
    const treatmentPlanDtoArray = response.data;
    return treatmentPlanDtoArray;
  } catch (e) {
    throw new Error(`Failed to fetch treatment plan template: ${e}`);
  }
}

//POST /treatment-plan/assignment
export async function assignTreatmentPlan({
  patientId,
  treatmentPlanInputDto,
}: {
  patientId: string;
  treatmentPlanInputDto: TreatmentPlanInputDto;
}): Promise<TreatmentPlanDto> {
  try {
    const response = await httpClient.post<TreatmentPlanDto>(
      '/treatment-plan/assignment',
      treatmentPlanInputDto,
      {
        headers: { 'x-user-id': patientId },
      },
    );
    const treatmentPlanDto = response.data;
    return treatmentPlanDto;
  } catch (e) {
    throw new Error(`Failed to assign treatment plan: ${e}`);
  }
}

// PATCH /treatment-plan/assignment/{plan_id}
//
// Edits the plan IN PLACE — same id, same rows. This is the only save path that
// is safe while the patient is streaming: the delete-and-recreate path mints a
// new plan id, and the app re-fetches the plan by id on every Continue, so it
// would 404 and terminate the session.
//
// A 409 (`plan_shape_changed`) means a slot's expected_movement_id disagrees
// with what the plan actually holds — the edit reordered or inserted, or another
// clinician changed the plan since it was loaded. Surfaced to the caller rather
// than retried; the two cases need different handling by a human.
export async function updateAssignedTreatmentPlan({
  planId,
  patientId,
  treatmentPlanInputDto,
}: {
  planId: string;
  patientId: string;
  treatmentPlanInputDto: TreatmentPlanInputDto;
}): Promise<TreatmentPlanDto> {
  const response = await httpClient.patch<TreatmentPlanDto>(
    `/treatment-plan/assignment/${planId}`,
    treatmentPlanInputDto,
    {
      headers: { 'x-user-id': patientId },
    },
  );
  return response.data;
}

//DELETE /treatment-plan/assignment
export async function deleteTreatmentPlan(treatmentPlanId: string, patientId: string) {
  try {
    await httpClient.delete(`/treatment-plan/assignment/${treatmentPlanId}`, {
      headers: { 'x-user-id': patientId },
    });
  } catch (e) {
    // Keep the server's status and details: saveAndAssign must tell "the plan is already gone" or
    // "it has results now" (the old plan may stay) from a real failure (the save must stop).
    throw Object.assign(new Error(`Failed to delete treatment plan: ${e}`), {
      status: axios.isAxiosError(e) ? e.response?.status : undefined,
      details: axios.isAxiosError(e) ? e.response?.data?.details : undefined,
    });
  }
}

//DELETE /treatment-plan/template
export async function deleteTreatmentPlanTemplate(treatmentPlanTemplateId: string) {
  try {
    await httpClient.delete(`/treatment-plan/template/${treatmentPlanTemplateId}`);
  } catch (e) {
    throw new Error(`Failed to delete treatment plan template: ${e}`);
  }
}

//PATCH /treatment-plan/template/{template_id}
export async function editTreatmentPlanTemplate(
  templateId: string,
  treatmentPlanTemplateInputDto: TreatmentPlanTemplateInputDto,
): Promise<TreatmentPlanTemplateDto> {
  try {
    const response = await httpClient.patch<TreatmentPlanTemplateDto>(
      `/treatment-plan/template/${templateId}`,
      treatmentPlanTemplateInputDto,
    );
    const treatmentPlanTemplateDto = response.data;
    return treatmentPlanTemplateDto;
  } catch (e) {
    throw new Error(`Failed to patch treatment plan template: ${e}`);
  }
}

//GET /treatment-plan/revisions
//
// Every in-place edit made to this patient's plans. The Session view uses it to render
// "Plan Updated in the middle. Reason: ..." against the session the edit landed in.
//
// Fetched ONCE per patient and grouped client-side, not once per session: a patient with months
// of history has many sessions and very few revisions, so per-session requests would be almost
// all empty responses.
export async function fetchPlanRevisions(patientId: string): Promise<PlanRevisionDto[]> {
  try {
    const response = await httpClient.get<{ revisions: PlanRevisionDto[] }>(
      '/treatment-plan/revisions',
      { headers: { 'x-user-id': patientId } },
    );
    return response.data?.revisions ?? [];
  } catch (e) {
    throw new Error(`Failed to fetch plan revisions: ${e}`);
  }
}
